import { getRoundedRate } from '@common/utils';
import { ToastType } from '@components';
import { DocumentRouteState } from '@mobileStore/pdfDocument/types';
import { useLocation, useNavigate } from '@solidjs/router';
import {
  IAdjustment,
  ILoadEditViewModel,
  IOrderViewModel,
  VendorContractViewModel,
} from '@typeDefinitions/index';
import {
  LineItemViewModel,
  OrderLineItem,
  VendorLineItem,
} from '@typeDefinitions/lineItemTypes';
import { PaymentViewModel } from '@typeDefinitions/paymentTypes';
import { handleToast } from '@utils/utils';
import { sum } from 'lodash';

const carrierPay = (item: LineItemViewModel[] | undefined) => {
  return (
    item?.reduce(
      (total, item) =>
        total + getRoundedRate(item.rate as number, item.quantity as number),
      0,
    ) || 0
  );
};

const vendorPay = (lineItems: VendorLineItem[] | undefined) => {
  if (lineItems && lineItems.length > 0) {
    return lineItems.reduce(
      (total, item) => total + getRoundedRate(item.rate, item.quantity),
      0,
    );
  }
};
const totalOrderExpenses = (
  loads: ILoadEditViewModel[],
  vendors: VendorContractViewModel[],
) => {
  return (
    sum(
      loads
        .filter((i) => i.status !== 'Void')
        .map((item) => {
          return carrierPay(item.lineItems ?? []);
        }),
    ) +
    sum(
      vendors
        .filter((vendor) => vendor.status !== 'Void')
        .map((vendor) => vendorPay(vendor.lineItems ?? []) || 0),
    )
  );
};

const customerPay = (lineItems: OrderLineItem[] | undefined) => {
  return lineItems && lineItems.length > 0
    ? lineItems.reduce(
        (acc: number, item: OrderLineItem) =>
          acc + getRoundedRate(Number(item.rate), Number(item.quantity)),
        0,
      )
    : 0;
};

const adjustmentsTotal = (adjustments: IAdjustment[] | undefined) => {
  return adjustments?.reduce((acc, curr) => acc + curr.amount, 0) ?? 0;
};

const computeTotalLineItems = (lineItems: OrderLineItem[] | undefined) => {
  return lineItems && lineItems.length > 0
    ? lineItems
        .filter((v) => v.operationType !== 'Delete')
        .reduce((acc, item) => {
          let rate = item.rate;
          if (rate === '' || rate === '.' || rate === '-') {
            rate = '0';
          }
          return acc + Number(rate) * Number(item.quantity);
        }, 0)
    : 0;
};
const computeTotalPayments = (payments: PaymentViewModel[] | undefined) => {
  return payments && payments.length > 0
    ? payments
        .filter((v) => v.operationType !== 'Delete')
        .reduce((acc, item) => Number(acc) + Number(item.amount), 0)
    : 0;
};

const handleCustomerError = (error: unknown, toastMessage: string) => {
  if (Array.isArray(error)) {
    const errorMessages = error.map((v) => {
      if (typeof v === 'string') {
        return v;
      } else if (typeof v === 'object' && v !== null && 'errorMessage' in v) {
        return (v as { errorMessage: string }).errorMessage;
      }
    });
    handleToast(ToastType.Error, errorMessages.join(', '));
  } else {
    handleToast(ToastType.Error, toastMessage);
  }
};

const hasLTLLoad = (order: IOrderViewModel | null): boolean => {
  return order?.loads.some((load) => load.mode === 'LTL') ?? false;
};

const useOpenDocument = () => {
  const navigate = useNavigate();
  const location = useLocation();

  return (path: string) => {
    const state: DocumentRouteState = { from: location.pathname };
    navigate(path, { state });
  };
};

const getAlignmentClassMobile = (
  isGlobalSearchOpen: boolean,
  hasError: boolean,
  useClass?: string,
) => {
  if (isGlobalSearchOpen) {
    return hasError ? 'top-[165px]' : useClass || 'top-[112px]';
  }
  return 'top-[56px]';
};

export {
  adjustmentsTotal,
  carrierPay,
  computeTotalLineItems,
  computeTotalPayments,
  customerPay,
  handleCustomerError,
  totalOrderExpenses,
  useOpenDocument,
  vendorPay,
  hasLTLLoad,
  getAlignmentClassMobile,
};
